// src/components/CustomerMenu.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useReactToPrint } from 'react-to-print';

const url = process.env.REACT_APP_SERVER_URL || "https://centralsoft.com.my";
const stallID = localStorage.getItem("stallID");
const username = localStorage.getItem("username");

export default function CustomerMenu() {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [tableNumber, setTableNumber] = useState("");
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const printRef = useRef();

  const PLACEHOLDER =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236c757d'%3ENo Image%3C/text%3E%3C/svg%3E";

  // Get image URL
  const getImageUrl = (imageURL) => {
    if (!imageURL || imageURL === "null" || imageURL === "undefined") {
      return PLACEHOLDER;
    }

    if (imageURL.startsWith('http') || imageURL.startsWith('data:')) {
      return imageURL;
    }

    const cleanImageURL = imageURL.replace(/^\//, '').trim();
    let filename;
    if (cleanImageURL.includes('/')) {
      filename = cleanImageURL.split('/').pop();
    } else {
      filename = cleanImageURL;
    }

    return `${url}/food-images/${filename}`;
  };


  // Safe numeric conversion
  const getSafePrice = (p) => {
    if (p === null || p === undefined || p === "") return 0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  };

  // Safe toFixed function
  const safeToFixed = (value, decimals = 2) => {
    const num = getSafePrice(value);
    return num.toFixed(decimals);
  };

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @media print {
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          margin: 0;
          padding: 5px;
        }
        .no-print { display: none !important; }
        .print-only { display: block !important; }
        .kitchen-ticket {
          width: 80mm;
          border: 1px solid #000;
          padding: 8px;
          margin: 0 auto;
          background: white;
        }
        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
      @media screen {
        .print-only { display: none !important; }
      }
    `,
    documentTitle: `Order-${currentOrder?.orderId || ''}`
  });

  // Fetch foods and addons
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [foodsRes, addonsRes] = await Promise.all([
          axios.get(`${url}/api/admin/foods`, { params: { stallID } }),
          axios.get(`${url}/api/admin/addons`, { params: { stallID } }),
        ]);

        // Normalize foods
        const normalizedFoods = (foodsRes.data || []).map((f) => {
          const id = f.id ?? f.ID ?? f._id ?? String(f.id);
          const foodName = f.foodName ?? f.name ?? f.food_name ?? "Unnamed";
          const rawPrice = getSafePrice(f.price);
          const rawProm = getSafePrice(f.prom_price ?? f.promPrice);
          const basePrice = rawProm > 0 ? rawProm : rawPrice;

          return {
            ...f,
            id: String(id),
            foodName,
            price: rawPrice,
            prom_price: rawProm,
            basePrice,
          };
        });

        setFoods(normalizedFoods);

        const uniqueCategories = [
          ...new Set(normalizedFoods.map((x) => x.category || "uncategorized")),
        ];
        setCategories(uniqueCategories);

        // Normalize addons
        const normalizedAddons = (addonsRes.data?.addons || addonsRes.data || []).map((a) => ({
          id: String(a.id ?? a._id ?? a.ID),
          name: a.name,
          price: getSafePrice(a.price),
          category: a.category || "extra",
        }));

        setAvailableAddons(normalizedAddons);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Add to cart function
  const addToCart = useCallback((food) => {
    setCart((prev) => {
      const id = String(food.id);
      const exists = prev.find((c) => String(c.id) === id);
      if (exists) {
        return prev.map((c) =>
          String(c.id) === id ? { ...c, quantity: c.quantity + 1 } : c
        );
      }

      const item = {
        id,
        foodName: food.foodName ?? food.name,
        basePrice: getSafePrice(food.basePrice ?? food.prom_price ?? food.price),
        price: getSafePrice(food.price),
        prom_price: getSafePrice(food.prom_price),
        quantity: 1,
        selectedAddons: [],
        addonsTotal: 0,
        finalPrice: getSafePrice(food.basePrice ?? food.prom_price ?? food.price),
        notes: "",
      };
      return [...prev, item];
    });
  }, []);

  // Update item quantity
  const updateItemQuantity = useCallback((itemId, change) => {
    setCart((prev) =>
      prev
        .map((item) =>
          String(item.id) === String(itemId)
            ? { ...item, quantity: Math.max(1, (item.quantity || 1) + change) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  }, []);

  // Remove item completely
  const removeItem = useCallback((itemId) => {
    setCart((prev) => prev.filter((i) => String(i.id) !== String(itemId)));
  }, []);

  // Handle addon dropdown selection
  const handleAddonDropdown = useCallback((itemId, addonId) => {
    if (!addonId) return;

    setCart((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(itemId)) return item;

        const addon = availableAddons.find((a) => String(a.id) === String(addonId));
        if (!addon) return item;

        const existingIndex = (item.selectedAddons || []).findIndex(
          (a) => String(a.id) === String(addon.id)
        );

        let updatedAddons = [...(item.selectedAddons || [])];
        if (existingIndex >= 0) {
          updatedAddons[existingIndex] = {
            ...updatedAddons[existingIndex],
            quantity: (updatedAddons[existingIndex].quantity || 0) + 1,
          };
        } else {
          updatedAddons.push({ ...addon, quantity: 1 });
        }

        const addonsTotal = updatedAddons.reduce(
          (s, a) => s + getSafePrice(a.price) * (a.quantity || 0),
          0
        );

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          finalPrice: getSafePrice(item.basePrice) + addonsTotal,
        };
      })
    );
  }, [availableAddons]);

  // Update addon quantity
  const updateAddonQuantity = useCallback((itemId, addonId, change) => {
    setCart((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(itemId)) return item;

        let updatedAddons = (item.selectedAddons || []).map((a) =>
          String(a.id) === String(addonId)
            ? { ...a, quantity: Math.max(0, (a.quantity || 0) + change) }
            : a
        );

        // Remove zero quantity addons
        updatedAddons = updatedAddons.filter((a) => (a.quantity || 0) > 0);

        const addonsTotal = updatedAddons.reduce(
          (s, a) => s + getSafePrice(a.price) * (a.quantity || 0),
          0
        );

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          finalPrice: getSafePrice(item.basePrice) + addonsTotal,
        };
      })
    );
  }, []);

  // Update notes
  const updateNotes = useCallback((itemId, notes) => {
    setCart((prev) =>
      prev.map((item) =>
        String(item.id) === String(itemId) ? { ...item, notes } : item
      )
    );
  }, []);

  // Calculate item total
  const calculateItemTotal = useCallback((item) => {
    const perUnit = getSafePrice(item.finalPrice ?? item.basePrice ?? item.price);
    const qty = item.quantity || 1;
    return perUnit * qty;
  }, []);

  // Cart total
  const cartTotal = cart.reduce((s, i) => s + calculateItemTotal(i), 0);

  // Submit order
  const submitOrder = async () => {
    if (!tableNumber) {
      alert("Please enter table number");
      return;
    }
    if (cart.length === 0) {
      alert("Cart is empty");
      return;
    }

    try {
      const orderData = {
        stallID: stallID || "unknown",
        tableNumber,
        total: cartTotal,
        status: "pending",
      };

      const orderRes = await axios.post(`${url}/api/admin/orders`, orderData);

      if (orderRes.data.success) {
        const orderId = orderRes.data.orderId;

        // Prepare items for order confirmation
        const orderItems = [];

        for (const item of cart) {
          const addonsText =
            item.selectedAddons && item.selectedAddons.length > 0
              ? `Add-ons: ${item.selectedAddons
                  .map((addon) => `${addon.quantity} × ${addon.name}`)
                  .join(", ")}`
              : "";

          const notes = item.notes ? (addonsText ? `${item.notes} | ${addonsText}` : item.notes) : addonsText;

          const itemData = {
            stallID,
            orderId,
            foodId: item.id,
            foodName: item.foodName,
            quantity: item.quantity,
            unitPrice: getSafePrice(item.finalPrice),
            notes,
            totalPrice: getSafePrice(item.finalPrice) * item.quantity,
            selectedAddons: item.selectedAddons || [],
            basePrice: item.basePrice,
            addonsTotal: item.addonsTotal
          };

          await axios.post(`${url}/api/admin/order-items`, itemData);
          orderItems.push(itemData);
        }

        // Set current order for confirmation and printing
        const currentOrderData = {
          orderId,
          stallID,
          tableNumber,
          items: orderItems,
          totalAmount: cartTotal,
          orderTime: new Date().toLocaleString('en-MY')
        };

        setCurrentOrder(currentOrderData);
        setShowOrderConfirmation(true);

        // Clear cart after successful order
        setCart([]);
        setTableNumber("");

      } else {
        alert("Failed to create order");
      }
    } catch (err) {
      console.error("Order error:", err);
      alert("Error placing order: " + (err?.response?.data?.error || err.message));
    }
  };

  // Back to menu function
  const handleBackToMenu = () => {
    setShowOrderConfirmation(false);
    setCurrentOrder(null);
  };

  const filteredFoods =
    selectedCategory === "All"
      ? foods
      : foods.filter(
          (f) => (f.category || "uncategorized") === selectedCategory
        );

  // Order Confirmation Component
  const OrderConfirmation = ({ order, onBack, onPrint, printRef }) => {
    return (
      <div className="container-fluid">
        <div className="no-print mb-4">
          <button
            className="btn btn-secondary me-2"
            onClick={onBack}
          >
            ← Back to Menu
          </button>
          <button
            className="btn btn-primary"
            onClick={onPrint}
          >
            🖨️ Print Order Receipt
          </button>
        </div>

        {/* Printable Receipt */}
        <div ref={printRef} className="kitchen-ticket">
          <div style={{ textAlign: "center", marginBottom: "10px", borderBottom: "1px solid #000", paddingBottom: "8px" }}>
            <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>{order.stallID.toUpperCase()} KITCHEN</h2>
            <p style={{ margin: "2px 0", fontSize: "10px" }}>ORDER RECEIPT</p>
          </div>

          <div style={{ marginBottom: "8px", borderBottom: "1px dashed #000", paddingBottom: "8px" }}>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Order #:</strong> {order.orderId}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Table #:</strong> {order.tableNumber}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Time:</strong> {order.orderTime}</p>
          </div>

          <div style={{ marginBottom: "8px" }}>
            {order.items.map((item, index) => (
              <div key={index} style={{ marginBottom: "10px", borderBottom: "1px dotted #000", paddingBottom: "5px" }}>
                <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
                  {item.quantity} x {item.foodName}
                </p>
                <p style={{ margin: "2px 0", fontSize: "10px" }}>
                  RM {safeToFixed(item.unitPrice)} × {item.quantity} = RM {safeToFixed(item.totalPrice)}
                </p>

                {item.notes && (
                  <p style={{ margin: "2px 0", fontSize: "9px", fontStyle: "italic" }}>
                    ** {item.notes}
                  </p>
                )}

                {item.selectedAddons && item.selectedAddons.length > 0 && (
                  <div style={{ marginLeft: "8px" }}>
                    {item.selectedAddons.map((addon, addonIndex) => (
                      <p key={addonIndex} style={{ margin: "1px 0", fontSize: "9px" }}>
                        + {addon.quantity} x {addon.name} (+RM {safeToFixed(addon.price)})
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ borderTop: "2px solid #000", paddingTop: "8px", textAlign: "center" }}>
            <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
              TOTAL: RM {safeToFixed(order.totalAmount)}
            </p>
          </div>

          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "8px" }}>
            <p>*** KITCHEN COPY ***</p>
            <p>Thank you for your order!</p>
          </div>
        </div>

        {/* Screen Confirmation */}
        <div className="no-print mt-4">
          <div className="alert alert-success">
            <h4 className="alert-heading">✅ Order Placed Successfully!</h4>
            <div className="row">
              <div className="col-md-6">
                <p><strong>Order ID:</strong> {order.orderId}</p>
                <p><strong>Table Number:</strong> {order.tableNumber}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Total Amount:</strong> RM {safeToFixed(order.totalAmount)}</p>
                <p><strong>Order Time:</strong> {order.orderTime}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Order Summary</h5>
            </div>
            <div className="card-body">
              {order.items.map((item, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">{item.quantity}x {item.foodName}</h6>
                      <p className="mb-1 text-success">
                        RM {safeToFixed(item.unitPrice)} × {item.quantity} = RM {safeToFixed(item.totalPrice)}
                      </p>
                      {item.selectedAddons && item.selectedAddons.length > 0 && (
                        <div className="mt-2">
                          <small className="text-muted">Add-ons:</small>
                          {item.selectedAddons.map((addon, addonIndex) => (
                            <div key={addonIndex} className="ms-2">
                              <small>+ {addon.quantity}x {addon.name} (+RM {safeToFixed(addon.price * addon.quantity)})</small>
                            </div>
                          ))}
                        </div>
                      )}
                      {item.notes && (
                        <div className="mt-1">
                          <small className="text-muted">Note: {item.notes}</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (showOrderConfirmation && currentOrder) {
    return (
      <OrderConfirmation
        order={currentOrder}
        onBack={handleBackToMenu}
        onPrint={handlePrint}
        printRef={printRef}
      />
    );
  }

  return (
    <div className="container-fluid">
      <div className="bg-primary text-white p-3 mb-4">
        <h1 className="text-center">🍽️ Food Ordering</h1>
        <p className="text-center mb-0">
          Stall ID: <strong>{stallID}</strong> | Stall Name: <strong>{username}</strong>
        </p>
      </div>

      <div className="row">
        {/* Menu */}
        <div className="col-md-8">
          <div className="mb-3">
            <div className="btn-group" role="group">
              <button
                className={`btn btn-sm ${
                  selectedCategory === "All" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setSelectedCategory("All")}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  className={`btn btn-sm ${
                    selectedCategory === c ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => setSelectedCategory(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="row">
            {filteredFoods.map((food) => (
              <div key={String(food.id)} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100">
                  <div style={{
                    height: 180,
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: 'hidden'
                  }}>
                    <img
                      src={getImageUrl(food.imageURL)}
                      alt={food.foodName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                      className="img-thumbnail"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.target.src = PLACEHOLDER;
                      }}
                    />
                  </div>
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">{food.foodName}</h5>
                    <p className="card-text flex-grow-1">{food.description}</p>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <div>
                        <strong>RM {safeToFixed(food.prom_price > 0 ? food.prom_price : food.price)}</strong>
                      </div>
                      <button
                        className="btn btn-success"
                        onClick={() => addToCart(food)}
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {foods.length === 0 && <p className="text-muted p-3">No foods found.</p>}
          </div>
        </div>

        {/* Cart */}
        <div className="col-md-4">
          <div className="card sticky-top" style={{ top: 20 }}>
            <div className="card-header bg-warning">
              <h5 className="mb-0">🛒 Your Cart</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Table Number *</label>
                <input
                  type="text"
                  className="form-control"
                  value={tableNumber}
                  onChange={(e) => setTableNumber(e.target.value)}
                  placeholder="e.g., T01"
                />
              </div>

              {cart.length === 0 ? (
                <p className="text-muted">Cart is empty</p>
              ) : (
                <div className="list-group mb-3">
                  {cart.map((item) => (
                    <div key={String(item.id)} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <div style={{ flex: 1 }}>
                          <h6 className="mb-1">{item.foodName}</h6>
                          <small className="text-muted d-block">
                            Base: RM {safeToFixed(item.basePrice)} × {item.quantity} = RM {safeToFixed(item.basePrice * item.quantity)}
                          </small>

                          {/* Add-on dropdown */}
                          <div className="mt-2">
                            <select
                              className="form-select form-select-sm"
                              defaultValue=""
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val) handleAddonDropdown(item.id, val);
                                e.target.value = ""; // reset dropdown
                              }}
                            >
                              <option value="">+ Add an add-on...</option>
                              {availableAddons.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name} (+RM {safeToFixed(a.price)})
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Selected add-ons list */}
                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <div className="mt-2 p-2 bg-light rounded">
                              {item.selectedAddons.map((a) => (
                                <div
                                  key={String(a.id)}
                                  className="d-flex justify-content-between align-items-center mb-1"
                                >
                                  <div>
                                    <small>{a.name}</small>
                                    <div className="text-muted small">
                                      + RM {safeToFixed(a.price)} each
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center">
                                    <button
                                      className="btn btn-sm btn-outline-secondary me-2"
                                      onClick={() => updateAddonQuantity(item.id, a.id, -1)}
                                    >
                                      -
                                    </button>
                                    <span className="mx-1">{a.quantity}</span>
                                    <button
                                      className="btn btn-sm btn-outline-secondary ms-2"
                                      onClick={() => updateAddonQuantity(item.id, a.id, +1)}
                                    >
                                      +
                                    </button>
                                  </div>

                                  <div style={{ width: 80, textAlign: "right" }}>
                                    <small className="fw-bold">
                                      RM {safeToFixed(a.price * (a.quantity || 0))}
                                    </small>
                                  </div>
                                </div>
                              ))}

                              <div className="border-top pt-1 mt-2 d-flex justify-content-between">
                                <small className="fw-bold">Add-ons total</small>
                                <small className="fw-bold text-primary">
                                  +RM {safeToFixed(item.addonsTotal)}
                                </small>
                              </div>
                            </div>
                          )}

                          {/* 📝 Notes input */}
                          <div className="mt-2">
                            <label className="form-label small text-muted mb-1">
                              Notes / Special Instructions:
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. No chili, less sugar..."
                              value={item.notes || ""}
                              onChange={(e) => updateNotes(item.id, e.target.value)}
                            />
                          </div>

                          {/* Item totals */}
                          <div className="mt-2 d-flex justify-content-between align-items-center">
                            <div>
                              <small className="text-muted">Qty: {item.quantity}</small>
                            </div>
                            <div>
                              <strong>RM {safeToFixed(calculateItemTotal(item))}</strong>
                            </div>
                          </div>
                        </div>

                        {/* Qty controls & remove */}
                        <div className="ms-3 d-flex flex-column align-items-center">
                          <div className="d-flex flex-column">
                            <button
                              className="btn btn-sm btn-outline-secondary mb-1"
                              onClick={() => updateItemQuantity(item.id, +1)}
                            >
                              +
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => updateItemQuantity(item.id, -1)}
                            >
                              -
                            </button>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger mt-2"
                            onClick={() => removeItem(item.id)}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-top pt-3">
                <div className="d-flex justify-content-between fw-bold fs-5">
                  <span>Total:</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <button className="btn btn-success w-100 mt-3" onClick={submitOrder} disabled={!tableNumber || cart.length === 0}>
                  📋 Place Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
